import Text from '@deprecated-ui/common/Text';
import TableCompany from '@deprecated-ui/company/Table';
import color from '@deprecated-ui/_style/color';
import device from '@deprecated-ui/_style/device';
import styled, { css, keyframes } from 'styled-components';

const ContentWrapper = styled.div`
  padding-bottom: 30px;
`;

const ContentContainer = styled.div`
  border: solid 1px ${color.gainsboro};
`;

const HeaderWrapper = styled.div`
  background-color: ${color.light};
  border-bottom: solid 1px ${color.gainsboro};
  padding: 20px;
`;

const HeaderTitle = styled(Text).attrs({
  as: 'h1',
  size: '36px',
  family: 'bold',
})`
  text-transform: capitalize;
  margin-right: 10px;
`;

const Th = styled(Text).attrs({ p: '15px 0px 15px 20px' })``;

const Td = styled(Text).attrs((props) => ({
  p: '0 0 0 20px',
  // @ts-expect-error
  family: props.bold && 'bold',
  height: '44px',
}))`
  background: #ffffff;
  a {
    color: inherit;
  }
`;

interface TableProps {
  isLoading?: boolean;
}

const Table = styled(TableCompany)<TableProps>`
  tr > td:last-child > p {
    padding-right: 10px;
  }
  .ant-table-content {
    overflow-x: auto;
  }

  .ant-table .ant-table-container::before,
  .ant-table .ant-table-container::after {
    display: none;
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  .ant-table-content::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  .ant-table-content {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }

  .ant-table-thead
    > tr
    > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before {
    background: transparent;
  }

  .ant-table-column-sort {
    background: transparent;
  }

  .ant-table-column-sorters {
    padding: 0px 5px 0px 0px;
    width: 100%;
    display: flex;

    & > span:nth-child(1) {
      width: 100%;
      margin-right: 1px;
    }

    .ant-table-column-sorter {
      .ant-table-column-sorter-up.active,
      .ant-table-column-sorter-down.active {
        color: ${color.highempahis};
        display: block;
      }
    }
  }

  .ant-table-column-sorter {
    visibility: hidden;
    margin-left: 0px;
  }

  th:hover {
    .ant-table-column-sorter {
      visibility: visible;
    }
  }

  .catalog-table-body-row.no-bottom-border {
    td {
      border-bottom-color: #ffffff !important;

      P {
        padding-left: 16px;
      }
    }
  }

  ${({ isLoading }) =>
    isLoading &&
    css`
      display: none;
    `}

  .hide-on-mobile {
    display: none;

    @media ${device.tablet} {
      display: table-cell;
    }
  }
`;

const Chip = styled.a`
  border: 1px solid ${color['primary60%']};
  color: ${color.primary};
  border-radius: 10px;
  display: inline-block;
  margin: 5px 10px 5px 0;
  padding: 0 10px;
  height: 20px
  line-height: 18px;
  transition: .1s;
  font-size: 13px;
  white-space: nowrap;

  &:hover {
    border-color: ${color.primary};
    background: ${color.primary};
    color: ${color.light};
  }
`;

const loadingAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const LoadingSpinner = styled.div`
  min-width: 100px;
  min-height: 100px;
  position: relative;
  :before {
    content: 'Loading…';
    position: absolute;
    top: calc(50% - 15px);
    left: calc(50% - 15px);
    width: 30px;
    height: 30px;
    margin-top: 0;
    margin-left: 0;
  }
  :before {
    content: '';
    border-radius: 50%;
    border-top: 2px solid ${color.veryLightGrey};
    border-right: 2px solid transparent;
    animation: ${loadingAnimation} 0.7s linear infinite;
    -webkit-animation: ${loadingAnimation} 0.7s linear infinite;
  }
`;

const DayTradeMultiplier = styled(Td)`
  min-height: 45px;
  display: flex;
  align-items: center;
`;

const MultiplierBadge = styled.span`
  display: flex;
  align-items: center;
  background: #f2f1fe;
  color: #8277f8;
  border-radius: 3px;
  border: 1px solid #8277f8;
  width: 35px;
  height: 18px;
  font-size: 12px;
  font-weight: bold;
  margin: 0;
  padding: 0 4.5px 0 2px;

  svg {
    height: 12px;
    width: 12px;
  }
`;

const BadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  > * {
    margin-left: 0 !important;
  }
`;

const SuspendedBadge = styled.span`
  border: 1px solid #ee4a49;
  border-radius: 2px;
  padding: 1px 6px;
  font-family: var(--font-regular);
  font-size: 10px;
  line-height: 12px;
  color: #ee4a49;
`;

const NewBadge = styled(SuspendedBadge)`
  border: 1px solid #00AB6B;
  color: #00AB6B;
`;

const UmaBadge = styled.div`
  display: flex;
  align-items: center;
  height: 12px;
  padding: 0;
  width: 22.8px;
  cursor: unset;
`;

const HaircutBadge = styled.div`
  border: 1px solid #94b041;
  font-family: var(--font-regular);
  font-weight: 700;
  padding: 0 4px;
  font-size: 12px;
  line-height: 16.2px;
  color: #94b041;
  border-radius: 2px;
  background: #f2f6e8;
`;

const HaircutCell = styled.div`
  display: flex;
  align-items: center;
  min-height: 44px;
`;

const UpdateBannerContainer = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #dadada;
  border-radius: 3px;
  padding: 9px 10px;
  font-family: var(--font-regular);
  font-size: 13px;
  line-height: 18px;
`;

const EmptyTableWrapper = styled.div`
  flex-direction: column;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-bottom: solid 1px #dadada;
`;

const PopoverInfoContainer = styled.div`
  width: 449px;
  border: 1px solid #dadada;
  padding: 20px;
`;

export {
  ContentWrapper,
  ContentContainer,
  HeaderWrapper,
  HeaderTitle,
  Th,
  Td,
  Table,
  Chip,
  LoadingSpinner,
  DayTradeMultiplier,
  MultiplierBadge,
  BadgeContainer,
  SuspendedBadge,
  NewBadge,
  UmaBadge,
  HaircutBadge,
  HaircutCell,
  UpdateBannerContainer,
  EmptyTableWrapper,
  PopoverInfoContainer,
};
